<template>
  <div>
    <div class="table-tabs pb-5">
      <b-tabs class="pb-0 pt-0">
        <b-tab v-if="assignment?.domesticItemsCount > 0" @click="setActiveTab()">
          <template #title>
            <mdicon name="TruckOutline" />
            Domestic shipments
            <span v-if="domesticErrorsCount" class="error-label">
              {{ domesticErrorsCount }}
            </span>
          </template>
        </b-tab>
        <b-tab
          v-if="assignment?.internationalItemsCount > 0"
          :disabled="assignment?.internationalItemsCount === 0"
          @click="setActiveTab()"
        >
          <template #title>
            <mdicon name="Earth" />
            International shipments
            <span v-if="internationalErrorsCount" class="error-label">
              {{ internationalErrorsCount }}
            </span>
          </template>
        </b-tab>
      </b-tabs>

      <div class="pb-5 mb-5">
        <ItemsTable :assignment-id="assignment.id" :destination-type="destinationType" :filter="searchData" />
      </div>

      <div v-if="assignment.domesticItemsCount || assignment.internationalItemsCount" class="tabs-filter pt-0">
        <SettingsButton />
        <SearchItemsBy @search="search" />
        <StripeBtn
          v-if="isActiveDeleting"
          class="only-icon ml-3"
          :disabled="checkedItems.length === 0 || isFinishedStatus"
          @click="deleteCheckedItems"
        >
          <mdicon size="18" name="TrashCanOutline" />
        </StripeBtn>
      </div>
    </div>
    <AssignmentFooter :key="`assignment-footer-${assignment.id}`" :destination-type="destinationType" />
    <LabelErrorsModal />
  </div>
</template>

<script lang="ts">
import type { Assignment } from '@/types/Models/Assignment'
import type { SearchFilter } from '@/types/Models/SearchFilterType'
import { notify } from '@kyvg/vue3-notification'
import { BTab, BTabs } from 'bootstrap-vue-next'
import { storeToRefs } from 'pinia'
import { defineComponent, ref, computed, watch, type Ref, type ComputedRef, onMounted } from 'vue'
import { DOMESTIC, INTERNATIONAL } from '@/const/AssignmentDestinationTypes'
import { OPEN, VALIDATION_COMPLETE } from '@/const/AssignmentStatuses'
import { LABELS_CREATED_WITH_ERRORS } from '@/const/LabelsCreatedStatuses'
import { DEFAULT_LOADER } from '@/const/LoadersNames'
import { LABEL_ERRORS_MODAL } from '@/const/ModalTypes'
import { none } from '@/services/Helpers'
import { openModal } from '@/services/Modal'
import { useAssignmentStore } from '@/store/assignment'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useItemStore } from '@/store/item'
import { useUiStore } from '@/store/ui'
import StripeBtn from '@/views/Components/Elements/Buttons/StripeBtn.vue'
import ItemsTable from '@/views/Components/Assignment/ItemsTable.vue'
import SearchItemsBy from '@/views/Components/Assignment/SearchItemsBy.vue'
import SettingsButton from '@/views/Components/Assignment/SettingsButton.vue'
import AssignmentFooter from '@/views/Components/Assignment/AssignmentFooter.vue'
import LabelErrorsModal from '@/views/Modals/Assignment/LabelErrorsModal.vue'

export default defineComponent({
  name: 'BatchItemsArea',
  components: {
    BTab,
    BTabs,
    StripeBtn,
    ItemsTable,
    SearchItemsBy,
    SettingsButton,
    AssignmentFooter,
    LabelErrorsModal,
  },
  setup() {
    const uiStore = useUiStore()
    const itemStore = useItemStore()
    const assignmentStore = useAssignmentStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()

    const { labelsCreatedStatus, internationalErrorsCount, domesticErrorsCount, assignmentStatus, isFinishedStatus } =
      storeToRefs(assignmentSummaryStore)

    const assignment: ComputedRef<Assignment> = computed(() => assignmentSummaryStore.assignment as Assignment)
    const checkedItems = computed(() => itemStore.checkedItems)
    const isActiveDeleting = computed(
      () =>
        [VALIDATION_COMPLETE, OPEN].includes(assignmentStatus.value) ||
        labelsCreatedStatus.value === LABELS_CREATED_WITH_ERRORS,
    )

    const labelErrorShown: Ref<boolean> = ref(false)
    const destinationType: Ref<string> = ref(assignment.value.domesticItemsCount > 0 ? DOMESTIC : INTERNATIONAL)
    const searchData: Ref<SearchFilter> = ref({ query: null, byTrackingStatus: null })

    const setActiveTab = () => {
      document.querySelectorAll('.nav-tabs button').forEach((el, i) => {
        if (el.classList.contains('active')) {
          destinationType.value = i ? DOMESTIC : INTERNATIONAL
        }
      })
      return true
    }

    const deleteCheckedItems = () => {
      if (!checkedItems.value.length) {
        notify({ group: 'error', text: 'Items not selected' })
        return
      }
      uiStore.loaderShow({ name: DEFAULT_LOADER })
      itemStore
        .deleteCheckedItems({ items: checkedItems.value })
        .then(() => {
          uiStore.loaderHide({ name: DEFAULT_LOADER })
          itemStore.resetCheckedItems()
          assignmentStore.refreshItems()
          assignmentSummaryStore
            .fetch(assignment.value.id)
            .then(() => {
              if (!itemStore.errorsOnly) {
                return
              }
              if (destinationType.value === DOMESTIC && assignment.value.domesticErrorsCount === 0) {
                itemStore.setErrorsOnly(false)
              }
              if (destinationType.value === INTERNATIONAL && assignment.value.internationalErrorsCount === 0) {
                itemStore.setErrorsOnly(false)
              }
            })
            .catch(none)
          notify({ group: 'success', text: 'Items was deleted!' })
        })
        .catch(() => {
          notify({ group: 'error', text: "Items wasn't deleted!" })
        })
        .finally(() => {
          uiStore.loaderHide({ name: DEFAULT_LOADER })
        })
    }

    const search = (params: SearchFilter) => {
      searchData.value = params
    }

    const showLabelErrorModal = () => {
      if (labelErrorShown.value) {
        return
      }
      if (assignment.value?.labelsErrorsCount) {
        labelErrorShown.value = true
        openModal(LABEL_ERRORS_MODAL)
      }
    }

    watch(
      assignment,
      (newAssignment) => {
        if (!newAssignment) {
          return
        }
        const { domesticItemsCount, internationalItemsCount } = newAssignment
        if (internationalItemsCount === 0) {
          destinationType.value = DOMESTIC
        } else if (domesticItemsCount === 0) {
          destinationType.value = INTERNATIONAL
        }
        showLabelErrorModal()
      },
      { deep: true },
    )

    onMounted(showLabelErrorModal)

    return {
      assignment,
      internationalErrorsCount,
      domesticErrorsCount,
      isActiveDeleting,
      isFinishedStatus,
      destinationType,
      checkedItems,
      searchData,
      deleteCheckedItems,
      setActiveTab,
      search,
    }
  },
})
</script>

<style lang="scss" scoped>
.table-tabs {
  position: relative;
}
.tabs-filter {
  position: absolute;
  top: 0;
  right: 0;
  margin-top: 14px;
  padding-top: 40px;
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
}

.error-label {
  margin-left: 18px;
  font-size: 11px;
  position: relative;
}

.error-label:after {
  content: '';
  position: absolute;
  width: 8px;
  height: 8px;
  background: #ff4e10;
  border-radius: 50%;
  left: -10px;
  top: 50%;
  margin-top: -5px;
}
:deep(.search-item) {
  input {
    border-left: none;
  }
  .custom-dropdown {
    border-right: 1px solid #cccccc !important;
  }
}
</style>
